import {useReducer, useEffect, useState} from 'react';
import {contestReducer, initialState} from "@/hooks/contest/contestReducer.js";
import {CONTEST_ACTIONS} from "@/hooks/contest/contestActions.js";
import {APP_STEPS} from "@/hooks/contest/contestSteps.js";
import {useContestLive} from "@/features/contest/hooks/useContestLive.js";
import {initializeContest} from "@/features/contest/services/initService.js";
import {syncUserContestStatus} from "@/features/contest/services/contestService.js";
import {calculatePotentialGains} from "@/utils/logic.js";
import {getParticipants, getPenaltyLeaderboard, joinContest, joinPenalty, submitHalfTimePrediction, submitPenaltyPrediction, getMyState, getReferral} from "@/api/participant.js";
import {mapBackendToFrontend} from "@/utils/mappers.js";
import {STORAGE_KEYS} from "@/utils/constants.js";
import {calculateRugbyPotentialGains} from "@/utils/rugbyLogic.js";

export function useContestLogic() {
    const [state, dispatch] = useReducer(contestReducer, initialState);

    // --- PARCOURS MI-TEMPS (opt-in, post-inscription) ---
    // La modale d'incitation s'affiche AU-DESSUS du classement (comme le quiz) : le joueur est
    // déjà inscrit, le prono mi-temps est ajouté via l'endpoint dédié (submitHalfTimePrediction).
    // Football : prono = score {team1, team2}. Rugby : prono = {selection, margin} (vainqueur + écart).
    const [userScoreHalfTime, setUserScoreHalfTime] = useState({team1: 0, team2: 0});
    const [userRugbyBetHalfTime, setUserRugbyBetHalfTime] = useState({selection: null, margin: 1});

    const isRugby = () => (state.contestData?.type || 'FOOTBALL') === 'RUGBY';

    // Le jeu mi-temps tourne pour le football ET le rugby (seule la saisie change).
    const isHalfTimeEnabled = () => Boolean(state.contestData?.halfTimeEnabled);

    // Mappe la sélection rugby (HOME/AWAY/DRAW) vers le payload API {winningTeamId, predictionMargin}.
    const rugbyBetToApi = (bet) => {
        const fixture = state.contestData?.fixture;
        let winningId = null;
        if (bet.selection === 'HOME') winningId = fixture?.homeTeam?.id ?? null;
        else if (bet.selection === 'AWAY') winningId = fixture?.awayTeam?.id ?? null;
        return {
            winningTeamId: winningId,
            predictionMargin: bet.selection === 'DRAW' ? 0 : bet.margin,
        };
    };

    useContestLive(state.contestData?.id, dispatch, state);

    // Resolves the referral code for the welcome screen, with a per-session cache: once resolved, no extra
    // API round-trip on every navigation (the code's validity only matters until the sign-up anyway).
    const resolveReferral = (refCode) => {
        const cacheKey = `${STORAGE_KEYS.REFERRAL_CODE}_resolution_${refCode}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                dispatch({type: CONTEST_ACTIONS.SET_REFERRAL, payload: JSON.parse(cached)});
                return;
            } catch { /* corrupt cache → re-fetch */ }
        }
        getReferral(refCode).then((res) => {
            if (!res) return;
            sessionStorage.setItem(cacheKey, JSON.stringify(res));
            dispatch({type: CONTEST_ACTIONS.SET_REFERRAL, payload: res});
        });
    };

    const initContest = (slug, contestId, sourceParam, magicToken, refParam) => {
        // Referral link (?ref=): persist the code and resolve it for the welcome screen. A referral visit
        // with no explicit ?src is attributed to the referral in tracking (source = "ref:<code>").
        if (refParam) {
            sessionStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, refParam);
        }
        const refCode = refParam || sessionStorage.getItem(STORAGE_KEYS.REFERRAL_CODE);

        // Strip the tracking/token params from the address bar in one place.
        if (sourceParam || magicToken || refParam) {
            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({path: cleanUrl}, "", cleanUrl);
        }

        let finalSource = sourceParam;
        if (finalSource) {
            sessionStorage.setItem(STORAGE_KEYS.CONTEST_SOURCE, finalSource);
        } else {
            finalSource = sessionStorage.getItem(STORAGE_KEYS.CONTEST_SOURCE);
            if (!finalSource && refCode) {
                // Referral arrival with no explicit ?src: PERSIST the channel, not just pass it to the
                // VIEW tracking — the JOIN reads `source` from storage at submit time, and without this
                // a referred sign-up would land with source=null in every acquisition stat.
                finalSource = `ref:${refCode}`;
                sessionStorage.setItem(STORAGE_KEYS.CONTEST_SOURCE, finalSource);
            }
        }

        if (refCode) {
            resolveReferral(refCode);
        }

        initializeContest(dispatch, slug, contestId, finalSource, magicToken, () => {
            console.log("Redirecting to Gojokodo...");
            const adminBaseUrl = import.meta.env.VITE_ADMIN_URL || "https://admin.gojokodo.com";
            window.location.assign(`${adminBaseUrl}/join/${slug}`);
        });
    };

    // --- 3. EFFETS DE BORD UI ---

    useEffect(() => {
        if (state.contestData?.type !== 'RUGBY') return;
        if (!state.contestData?.fixture?.odds) return;

        const {selection, margin} = state.userRugbyBet;
        const newCalcs = calculateRugbyPotentialGains(selection, margin, state.contestData.fixture.odds);

        dispatch({type: CONTEST_ACTIONS.UPDATE_CALCULATIONS, payload: newCalcs});
    }, [state.userRugbyBet, state.contestData]);

    useEffect(() => {
        if (state.contestData?.type === 'RUGBY') return;

        if (!state.contestData?.fixture?.odds) return;

        const newCalcs = calculatePotentialGains(state.userScore, state.contestData.fixture.odds);
        dispatch({type: CONTEST_ACTIONS.UPDATE_CALCULATIONS, payload: newCalcs});
    }, [state.userScore, state.contestData]);

    // --- 4. HANDLERS ---

    const handleRugbyBetChange = (field, value) => {
        dispatch({
            type: CONTEST_ACTIONS.UPDATE_RUGBY_BET,
            payload: {[field]: value}
        });
    };

    const handleFormSubmit = async (e, extraData = {}) => {
        e.preventDefault();

        dispatch({type: CONTEST_ACTIONS.SET_LOADING, payload: true});

        dispatch({type: CONTEST_ACTIONS.SET_FORM_ERROR, payload: null});

        try {
            const source = sessionStorage.getItem(STORAGE_KEYS.CONTEST_SOURCE);
            const contestType = state.contestData.type || 'FOOTBALL';

            let betData;

            if (contestType === 'RUGBY') {
                const ft = rugbyBetToApi(state.userRugbyBet);
                betData = {
                    winningTeamId: ft.winningTeamId,
                    predictionMargin: ft.predictionMargin
                };
            } else {
                // FOOTBALL
                betData = {
                    team1: state.userScore.team1,
                    team2: state.userScore.team2
                };
            }

            const data = {
                ...state.userInfo,
                ...betData,
                source: source,
                referralCode: sessionStorage.getItem(STORAGE_KEYS.REFERRAL_CODE),
                captchaToken: extraData?.captchaToken
            };

            const joinResponse = await joinContest(contestType, state.contestData.id, data);
            // referralApplied lets the confirmation honestly promise the reward email (false when the code
            // was invalid, capped, budget-exhausted, or the email was already known — all silent-fail).
            dispatch({
                type: CONTEST_ACTIONS.SET_REFERRAL_APPLIED,
                payload: Boolean(joinResponse?.referralApplied)
            });

            // Le statut a changé (CAN_JOIN_QUIZ → JOINED_PENDING_QUIZ) : on re-sync pour
            // rafraîchir canPlayQuiz/hasZone et router (sinon l'overlay quiz n'apparaît qu'au refresh)
            await syncUserContestStatus(state.contestData.id, state.contestData.status, dispatch);

            // Rechargement forcé post-inscription (Sécurité)
            const rawList = await getParticipants(state.contestData.id);
            const formatted = mapBackendToFrontend(rawList);

            dispatch({type: CONTEST_ACTIONS.UPDATE_LEADERBOARD, payload: {list: formatted}});
            dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.LEADERBOARD});
            return true;

        } catch (err) {
            let errorPayload = err;
            if (err.message && err.message.includes("{")) {
                try {
                    errorPayload = JSON.parse(err.message);
                } catch (e) {
                    console.warn("Impossible de parser l'erreur API", e);
                }
            }
            dispatch({type: CONTEST_ACTIONS.SET_FORM_ERROR, payload: errorPayload});
            return false;
        } finally {
            dispatch({type: CONTEST_ACTIONS.SET_LOADING, payload: false});
        }
    };

    // Validation du prono : si déjà en session → join direct (sans captcha ni form), sinon StepForm
    const submitProno = async () => {
        if (state.isAnonymous) {
            dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.FORM});
            return;
        }
        const ok = await handleFormSubmit({preventDefault: () => {}}, {});
        // Filet de sécurité : session invalide côté back → on retombe sur le form classique
        if (!ok) dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.FORM});
    };

    const handleScoreChange = (team, op) => {
        // Logique UI simple, peut rester ici ou aller dans le reducer
        const current = state.userScore[team];
        const min = state.matchScore[team];
        let next = op === 'plus' ? current + 1 : current - 1;
        if (next < min) next = min;

        dispatch({
            type: CONTEST_ACTIONS.UPDATE_USER_INFOS,
            payload: {score: {[team]: next}}
        });
    };

    // --- HANDLERS MI-TEMPS ---
    // Borne basse uniquement : htTeam >= scoreLive (pas de plafond, comme la saisie globale).
    const handleHalfTimeScoreChange = (team, op) => {
        setUserScoreHalfTime(prev => {
            const min = state.matchScore[team] || 0;
            let next = op === 'plus' ? prev[team] + 1 : prev[team] - 1;
            if (next < min) next = min;
            return {...prev, [team]: next};
        });
    };

    // Rugby : prono mi-temps = vainqueur + écart (mêmes contrôles que le prono final).
    const handleHalfTimeRugbyBetChange = (field, value) => {
        setUserRugbyBetHalfTime(prev => ({...prev, [field]: value}));
    };

    // Valeur de départ = score live (le minimum cohérent).
    const initialHalfTimeScore = () => ({
        team1: state.matchScore.team1 || 0,
        team2: state.matchScore.team2 || 0,
    });

    // Initialise la saisie mi-temps (football : score live ; rugby : reprend le prono final comme défaut).
    const initHalfTimeBets = () => {
        setUserScoreHalfTime(initialHalfTimeScore());
        setUserRugbyBetHalfTime({
            selection: state.userRugbyBet?.selection || 'HOME',
            margin: state.userRugbyBet?.margin || 1,
        });
    };

    // Ouvre la saisie mi-temps (depuis la modale d'incitation OU le bandeau du classement).
    const goToHalfTimeBetting = () => {
        initHalfTimeBets();
        dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.HT_BETTING});
    };

    // Renonce à la mi-temps depuis la saisie → retour au classement (le joueur est déjà inscrit).
    const skipHalfTime = () => {
        dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.LEADERBOARD});
    };

    // Ajoute le prono mi-temps à la participation existante (endpoint dédié), puis retour au classement.
    const validateHalfTime = async () => {
        try {
            const payload = isRugby()
                ? rugbyBetToApi(userRugbyBetHalfTime)
                : {homeScore: userScoreHalfTime.team1, awayScore: userScoreHalfTime.team2};
            await submitHalfTimePrediction(state.contestData.id, payload);
            const rawList = await getParticipants(state.contestData.id);
            dispatch({type: CONTEST_ACTIONS.UPDATE_LEADERBOARD, payload: {list: mapBackendToFrontend(rawList)}});
        } catch (err) {
            console.error("Erreur prono mi-temps", err);
        }
        dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.LEADERBOARD});
    };

    // Gain potentiel mi-temps (football) : sur les odds HT si dispo, sinon retombe sur les odds
    // full-time (comme le rugby) pour toujours afficher le gain, comme sur la saisie finale.
    const halfTimeOddsForGain = state.contestData?.fixture?.halfTimeOdds?.length
        ? state.contestData.fixture.halfTimeOdds
        : state.contestData?.fixture?.odds;
    const halfTimeGains = halfTimeOddsForGain?.length
        ? calculatePotentialGains(userScoreHalfTime, halfTimeOddsForGain)
        : null;

    // --- TIRS AU BUT ---
    // Poll-only (pas de SSE pénos) : /me est la source de vérité, on la relit toutes les 2 s pendant la
    // séance (filet "horloge suisse"), + le leaderboard pour le mini-classement. Coupé hors séance.
    const penaltyActive = Boolean(state.penaltyState && !state.penaltyState.finished);

    const refreshPenalty = async () => {
        const contestId = state.contestData?.id;
        if (!contestId) return;
        const {data: myState} = await getMyState(contestId);
        if (myState) {
            dispatch({type: CONTEST_ACTIONS.SET_PENALTY_STATE, payload: myState.penalty ?? null});
            dispatch({type: CONTEST_ACTIONS.SET_PENALTY_REWARD, payload: myState.penaltyReward ?? null});
            // Le lot FT est distribué à l'entrée des pénos : on le pousse aussi pour l'écran résultat.
            if (myState.reward) dispatch({type: CONTEST_ACTIONS.SET_USER_REWARD, payload: myState.reward});
        }
        // Classement FT (pour l'écran résultat) + classement dédié des tirs au but (pour l'overlay).
        const [rawList, penaltyRows] = await Promise.all([
            getParticipants(contestId),
            getPenaltyLeaderboard(contestId),
        ]);
        dispatch({type: CONTEST_ACTIONS.UPDATE_LEADERBOARD, payload: {list: mapBackendToFrontend(rawList)}});
        dispatch({type: CONTEST_ACTIONS.SET_PENALTY_BOARD, payload: penaltyRows ?? []});
    };

    // Joue un tir : optimiste (le serveur tranche), puis resync immédiat.
    const submitPenalty = async (kickId, prediction) => {
        try {
            await submitPenaltyPrediction(state.contestData.id, kickId, prediction);
        } catch (err) {
            console.error("Erreur prono tir au but", err);
        }
        await refreshPenalty();
    };

    // Latecomer : inscription "pénos seulement" (pseudo + email + captcha), puis on tire l'état pour
    // basculer directement sur l'overlay tirs au but. formData = {pseudo, email, captchaToken, source}.
    const joinForPenalty = async (formData) => {
        const source = sessionStorage.getItem(STORAGE_KEYS.CONTEST_SOURCE);
        // Only the join itself is critical → its failure (captcha, already-played…) propagates to the form.
        const joinResponse = await joinPenalty(state.contestData.id, {
            ...formData,
            source,
            referralCode: sessionStorage.getItem(STORAGE_KEYS.REFERRAL_CODE),
        });
        // Same honest confirmation as the standard flow: the reward toast only when attribution happened.
        dispatch({
            type: CONTEST_ACTIONS.SET_REFERRAL_APPLIED,
            payload: Boolean(joinResponse?.referralApplied)
        });
        // The join succeeded; a transient refresh hiccup must NOT surface as an error (the 2s poller recovers).
        try {
            await syncUserContestStatus(state.contestData.id, state.contestData.status, dispatch);
            await refreshPenalty();
        } catch (err) {
            console.error("Post-join penalty refresh failed (poller will recover)", err);
        }
    };

    useEffect(() => {
        if (!penaltyActive) return;
        const intervalId = setInterval(() => { refreshPenalty(); }, 2000);
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [penaltyActive, state.contestData?.id]);

    return {
        game: {
            data: state.contestData,
            scores: state.matchScore,
            // FOOT
            userBet: state.userScore,
            handleScoreChange,
            // RUGBY
            userRugbyBet: state.userRugbyBet,
            handleRugbyBetChange,
            calculations: state.calculations
        },
        ui: {
            status: state.status,
            step: state.step,
            setStep: (s) => dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: s}),
            isLoading: state.isLoading,
            error: state.error,
            isGoalFlashing: state.overlays.goal,
            canPlayQuiz: state.canPlayQuiz,
            hasZone: state.hasZone,
            isAnonymous: state.isAnonymous,
            goToQuiz: () => dispatch({type: CONTEST_ACTIONS.SET_STEP, payload: APP_STEPS.QUIZ})
        },
        user: {
            info: state.userInfo,
            setInfo: (info) => dispatch({type: CONTEST_ACTIONS.UPDATE_USER_INFOS, payload: {info}}),
            submit: handleFormSubmit,
            isAnonymous: state.isAnonymous,
            submitProno,
            halfTimeReward: state.halfTimeReward,
            // Referral resolution of the invitation link + whether an attribution happened at sign-up.
            referral: state.referral,
            referralApplied: state.referralApplied
        },
        halfTime: {
            enabled: isHalfTimeEnabled(),
            score: userScoreHalfTime,
            rugbyBet: userRugbyBetHalfTime,
            handleRugbyBetChange: handleHalfTimeRugbyBetChange,
            rewardLabel: state.contestData?.participantUi?.halfTimeRewardLabel,
            handleScoreChange: handleHalfTimeScoreChange,
            gains: halfTimeGains,
            play: goToHalfTimeBetting,   // depuis la modale d'incitation OU le bandeau "rejouer"
            skip: skipHalfTime,          // bouton retour de la saisie
            validate: validateHalfTime,
        },
        leaderboard: {
            fixtureStartingAt: state.contestData?.fixture?.startingAt,
            list: state.leaderboard,
            fullList: state.leaderboardFull,
            currentUser: state.userRankData,
        },
        penalty: {
            state: state.penaltyState,   // {finished, openKick, myPrediction, kicks[], myScore, myRank} | null
            reward: state.penaltyReward,
            board: state.penaltyBoard,   // classement dédié des tirs au but [{rank, pseudo, isMe, score, kicks}]
            submit: submitPenalty,       // (kickId, prediction) => ...
            join: joinForPenalty,        // latecomer : (formData) => inscription pénos puis bascule overlay
        },
        overlays: {
            goal: state.overlays.goal,
            qr: state.overlays.qr,
            setQr: (data) => dispatch({type: CONTEST_ACTIONS.SET_QR_OVERLAY, payload: data}),
            selectedPlayer: state.overlays.selectedPlayer,
            setSelectedPlayer: (p) => dispatch({type: CONTEST_ACTIONS.SET_SELECTED_PLAYER, payload: p})
        },
        initContest,
        // Re-évalue le statut participant (ex: après le quiz : cookie posé, bonus quiz appliqué).
        // On recharge aussi le leaderboard car le quiz vient de modifier les scores.
        refreshStatus: async () => {
            if (!state.contestData?.id) return;
            const {step} = await syncUserContestStatus(state.contestData.id, state.contestData.status, dispatch);
            if (step === APP_STEPS.LEADERBOARD) {
                const rawList = await getParticipants(state.contestData.id);
                dispatch({type: CONTEST_ACTIONS.UPDATE_LEADERBOARD, payload: {list: mapBackendToFrontend(rawList)}});
            }
        }
    };
}